import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";
import { useSearchParamsHandler } from "@/hooks/use-search-params-handler.ts";
import { WalletType } from "@providers/wallet";
import { WalletSortColumn } from "@api/wallet-screener/wallet-screener.ts";

export type WalletDiscoveryFiltersParams = {
  invested?: {
    min?: number;
    max?: number;
  };
  trades?: {
    min?: number;
    max?: number;
  };
  realizedPnl?: {
    min?: number;
    max?: number;
  };
  realizedPnlPercent?: {
    min?: number;
    max?: number;
  };
  unrealizedPnl?: {
    min?: number;
    max?: number;
  };
  unrealizedPnlPercent?: {
    min?: number;
    max?: number;
  };
  netPnl?: {
    min?: number;
    max?: number;
  };
  netPnlPercent?: {
    min?: number;
    max?: number;
  };
  peakPnl?: {
    min?: number;
    max?: number;
  };
  peakPnlPercent?: {
    min?: number;
    max?: number;
  };
  winRate?: {
    min?: number;
    max?: number;
  };
  sort: {
    column: WalletSortColumn;
    direction: string;
  };
};

export const getWalletDiscoveryDefaultFilters =
  (): WalletDiscoveryFiltersParams => {
    return {
      invested: {
        min: undefined,
        max: undefined,
      },
      trades: {
        min: undefined,
        max: undefined,
      },
      realizedPnl: {
        min: undefined,
        max: undefined,
      },
      realizedPnlPercent: {
        min: undefined,
        max: undefined,
      },
      unrealizedPnl: {
        min: undefined,
        max: undefined,
      },
      unrealizedPnlPercent: {
        min: undefined,
        max: undefined,
      },
      netPnl: {
        min: undefined,
        max: undefined,
      },
      netPnlPercent: {
        min: undefined,
        max: undefined,
      },
      peakPnl: {
        min: undefined,
        max: undefined,
      },
      peakPnlPercent: {
        min: undefined,
        max: undefined,
      },
      winRate: {
        min: undefined,
        max: undefined,
      },
      sort: {
        column: "netPnlPercent",
        direction: "desc",
      },
    };
  };

const WalletDiscoveryFilterContext = createContext<{
  filters: WalletDiscoveryFiltersParams;
  onFiltersUpdate: Dispatch<SetStateAction<WalletDiscoveryFiltersParams>>;
  searchWallet?: string | null;
  onSearchWalletUpdate: (address: string | undefined) => void;
  timeFrame: number;
  onTimeFrameUpdate: (timeFrame: number) => void;
  chains: WalletType[];
  onChainsUpdate: Dispatch<SetStateAction<WalletType[]>>;
  showStarred: boolean;
  setShowStarred: (value: boolean) => void;
  fetchedWallets: Map<string, number>;
  setFetchedWallets: Dispatch<SetStateAction<Map<string, number>>>;
  selectedWallets: Map<string, boolean>;
  setSelectedWallets: Dispatch<SetStateAction<Map<string, boolean>>>;
}>({
  filters: getWalletDiscoveryDefaultFilters(),
  onFiltersUpdate: () => {},
  searchWallet: null,
  onSearchWalletUpdate: () => {},
  timeFrame: 5,
  onTimeFrameUpdate: () => {},
  chains: [WalletType.SOL, WalletType.ETH],
  onChainsUpdate: () => {},
  showStarred: false,
  setShowStarred: () => {},
  fetchedWallets: new Map(),
  setFetchedWallets: () => {},
  selectedWallets: new Map(),
  setSelectedWallets: () => {},
});

export const useWalletDiscoveryFilter = () => {
  const context = useContext(WalletDiscoveryFilterContext);
  if (!context) {
    throw new Error(
      "useWalletDiscoveryFilter must be used within a WalletDiscoveryFilterProvider",
    );
  }
  return context;
};

const WalletDiscoveryFilterProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [filters, setFilters] = useState<WalletDiscoveryFiltersParams>(
    getWalletDiscoveryDefaultFilters(),
  );
  const [timeFrame, setTimeFrame] = useState<number>(7);
  const [chains, setChains] = useState<WalletType[]>([
    WalletType.SOL,
    WalletType.ETH,
  ]);
  const [showStarred, setShowStarred] = useState<boolean>(false);
  const [fetchedWallets, setFetchedWallets] = useState<Map<string, number>>(
    new Map(),
  );
  const [selectedWallets, setSelectedWallets] = useState<Map<string, boolean>>(
    new Map(),
  );

  const { inputValue: searchWallet, setInputValue: setSearchWallet } =
    useSearchParamsHandler().useDebouncedParam("searchWallet", 500);

  const onSearchWalletUpdate = useCallback(
    (searchWallet: string | undefined | null) => {
      setSearchWallet(searchWallet ?? "");
    },
    [],
  );

  const onTimeFrameUpdate = useCallback((newTimeFrame: number) => {
    setTimeFrame(newTimeFrame);
  }, []);

  return (
    <WalletDiscoveryFilterContext.Provider
      value={{
        filters,
        onFiltersUpdate: setFilters,
        searchWallet,
        onSearchWalletUpdate,
        timeFrame,
        onTimeFrameUpdate,
        chains,
        onChainsUpdate: setChains,
        showStarred,
        setShowStarred,
        fetchedWallets,
        setFetchedWallets,
        selectedWallets,
        setSelectedWallets,
      }}
    >
      {children}
    </WalletDiscoveryFilterContext.Provider>
  );
};

export default WalletDiscoveryFilterProvider;
